import { useMemo, useState } from "react"
import { Link, Navigate, useParams } from "react-router-dom"
import { useData } from "../data/DataContext"
import {
  buildRagIndex,
  getCurrentRag,
  getHistory,
  ragLabel,
  reportingYearForDate,
  type Rag,
  type RagIndex,
} from "../components/ObjectiveCard"
import type { Objective, ObjectiveStatus, Person } from "../data/types"
import { isReportablePerson } from "../data/types"

const STATUS_ORDER: Record<ObjectiveStatus, number> = { Active: 0, Draft: 1, Deleted: 2 }

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const CURRENT_MONTH_IDX = new Date().getMonth()

function ragCellClass(r: Rag): string {
  if (r === "G") return "bg-rag-green"
  if (r === "A") return "bg-rag-amber"
  if (r === "R") return "bg-rag-red"
  return "bg-rag-unset opacity-40"
}

function ragPillClass(r: Rag): string {
  if (r === "G") return "bg-rag-green-bg text-rag-green"
  if (r === "A") return "bg-rag-amber-bg text-rag-amber-ink"
  if (r === "R") return "bg-rag-red-bg text-rag-red"
  return "bg-rag-unset-bg text-ink-3"
}

function ragDotClass(r: Rag): string {
  if (r === "G") return "bg-rag-green"
  if (r === "A") return "bg-rag-amber"
  if (r === "R") return "bg-rag-red"
  return "bg-rag-unset"
}

export function CategoryPage() {
  const { name } = useParams<{ name: string }>()
  const categoryName = name ? decodeURIComponent(name) : ""
  const { people, objectives, ragScores } = useData()

  const reportingYear = reportingYearForDate()
  const ragIndex = useMemo(
    () => buildRagIndex(ragScores, reportingYear),
    [ragScores, reportingYear],
  )

  const categoryObjectives = useMemo(
    () => objectives.filter((o) => o.category === categoryName && o.status !== "Deleted"),
    [objectives, categoryName],
  )

  const years = useMemo(() => {
    const s = new Set<number>()
    for (const o of categoryObjectives) if (o.year != null) s.add(o.year)
    return [...s].sort((a, b) => b - a)
  }, [categoryObjectives])

  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const effectiveYear = selectedYear ?? years[0] ?? null

  const yearObjectives = useMemo(() => {
    if (effectiveYear == null) return categoryObjectives
    return categoryObjectives.filter((o) => o.year === effectiveYear)
  }, [categoryObjectives, effectiveYear])

  const peopleByName = useMemo(() => {
    const map = new Map<string, Person>()
    for (const p of people) map.set(p.fullName, p)
    return map
  }, [people])

  const byPerson = useMemo(() => {
    const map = new Map<string, Objective[]>()
    for (const o of yearObjectives) {
      const arr = map.get(o.personName) ?? []
      arr.push(o)
      map.set(o.personName, arr)
    }
    for (const arr of map.values()) {
      arr.sort(
        (a, b) =>
          (a.status ? STATUS_ORDER[a.status] : 99) -
          (b.status ? STATUS_ORDER[b.status] : 99),
      )
    }
    return map
  }, [yearObjectives])

  const activePeople = useMemo(
    () =>
      [...people]
        .filter(isReportablePerson)
        .sort((a, b) => {
          if (a.team !== b.team) return a.team === "Exec" ? -1 : 1
          return a.fullName.localeCompare(b.fullName)
        }),
    [people],
  )

  const peopleWithObjectives = activePeople.filter((p) => byPerson.has(p.fullName))
  const peopleWithoutObjectives = activePeople.filter((p) => !byPerson.has(p.fullName))

  const orphanNames = [...byPerson.keys()].filter((n) => {
    const p = peopleByName.get(n)
    return !p || !p.active
  })

  if (!categoryName) return <Navigate to="/categories" replace />

  const coveragePct =
    activePeople.length > 0
      ? Math.round((peopleWithObjectives.length / activePeople.length) * 100)
      : 0

  return (
    <div>
      {/* Back link */}
      <Link
        to="/categories"
        className="mb-[18px] inline-flex items-center gap-[6px] font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-3 transition hover:text-brass"
      >
        <span aria-hidden>&larr;</span> Categories
      </Link>

      {/* Hero */}
      <section className="mb-[26px] flex flex-wrap items-end justify-between gap-[30px] rounded-lg border border-rule bg-card px-[26px] py-[22px] shadow-card">
        <div className="min-w-0">
          <div className="mb-[6px] font-mono text-[10px] uppercase tracking-[0.16em] text-brass">
            Category
          </div>
          <h1 className="font-display text-[28px] font-medium leading-[1.15] tracking-[-0.015em] text-ink">
            {categoryName}
          </h1>
          <div className="mt-[6px] font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">
            {yearObjectives.length} objective{yearObjectives.length === 1 ? "" : "s"} &middot;{" "}
            {peopleWithObjectives.length} of {activePeople.length} active people covered
            {peopleWithoutObjectives.length > 0 ? (
              <>
                {" "}
                &middot;{" "}
                <span className="text-rag-amber-ink">
                  {peopleWithoutObjectives.length} gap
                  {peopleWithoutObjectives.length === 1 ? "" : "s"}
                </span>
              </>
            ) : null}
          </div>
        </div>

        {/* Coverage bar + year selector */}
        <div className="flex items-end gap-[18px]">
          <div className="min-w-[160px]">
            <div className="mb-[6px] flex items-baseline justify-between font-mono text-[10px] uppercase tracking-[0.1em] text-ink-3">
              <span>Coverage</span>
              <span className="font-sans text-[16px] font-medium normal-case tracking-normal text-ink">
                {coveragePct}%
              </span>
            </div>
            <div className="h-[6px] w-full overflow-hidden rounded-full bg-paper-sunk">
              <div
                className={`h-full transition-all ${
                  coveragePct >= 90
                    ? "bg-rag-green"
                    : coveragePct >= 50
                      ? "bg-rag-amber"
                      : "bg-rag-red"
                }`}
                style={{ width: `${coveragePct}%` }}
              />
            </div>
          </div>
          {years.length > 0 ? (
            <label className="flex flex-col gap-[6px]">
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
                Year
              </span>
              <select
                value={effectiveYear ?? ""}
                onChange={(e) =>
                  setSelectedYear(e.target.value ? Number(e.target.value) : null)
                }
                className="rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </section>

      {/* Coverage cards */}
      {yearObjectives.length === 0 ? (
        <div className="rounded-lg border border-dashed border-rule-strong bg-card p-8 text-center font-mono text-[11px] uppercase tracking-[0.12em] text-ink-3">
          No objectives recorded in this category
        </div>
      ) : (
        <>
          <div className="mb-8">
            <div className="mb-[14px] font-mono text-[10px] uppercase tracking-[0.16em] text-brass">
              Covered &middot; {peopleWithObjectives.length}
            </div>
            <div className="grid grid-cols-2 gap-[14px]">
              {peopleWithObjectives.map((p) => (
                <PersonCategoryCard
                  key={p.id}
                  person={p}
                  objectives={byPerson.get(p.fullName) ?? []}
                  ragIndex={ragIndex}
                />
              ))}
              {orphanNames.map((n) => (
                <PersonCategoryCard
                  key={n}
                  person={peopleByName.get(n) ?? null}
                  personName={n}
                  objectives={byPerson.get(n) ?? []}
                  ragIndex={ragIndex}
                  inactive
                />
              ))}
            </div>
          </div>

          {peopleWithoutObjectives.length > 0 ? (
            <div>
              <div className="mb-[14px] font-mono text-[10px] uppercase tracking-[0.16em] text-rag-amber-ink">
                Gaps &middot; {peopleWithoutObjectives.length}
              </div>
              <div className="rounded-lg border border-rule bg-card p-[18px] shadow-card">
                <p className="mb-[14px] text-[13px] text-ink-2">
                  No {categoryName} objective recorded for these active people. Click any name
                  to open their page and add one.
                </p>
                <div className="flex flex-wrap gap-2">
                  {peopleWithoutObjectives.map((p) => (
                    <Link
                      key={p.id}
                      to={`/person/${p.id}`}
                      className="inline-flex items-center gap-[7px] rounded-full border border-dashed border-rule-strong bg-paper-sunk px-[11px] py-[5px] text-[12px] text-ink-2 transition hover:border-brass hover:bg-card hover:text-ink"
                    >
                      {p.fullName}
                      {p.team ? (
                        <span className="font-mono text-[9.5px] uppercase tracking-[0.08em] text-ink-3">
                          {p.team}
                        </span>
                      ) : null}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}

/* ---------- PersonCategoryCard ---------- */

function PersonCategoryCard({
  person,
  personName,
  objectives,
  ragIndex,
  inactive,
}: {
  person: Person | null
  personName?: string
  objectives: Objective[]
  ragIndex: RagIndex
  inactive?: boolean
}) {
  const displayName = person?.fullName ?? personName ?? "Unknown"
  // Pick first Active, else Draft, else first.
  const primary =
    objectives.find((o) => o.status === "Active") ??
    objectives.find((o) => o.status === "Draft") ??
    objectives[0]

  const rag: Rag = primary ? getCurrentRag(primary, ragIndex) : null
  const history: Rag[] = primary ? getHistory(primary, ragIndex) : (Array(12).fill(null) as Rag[])

  const teamPill =
    person?.team === "Exec"
      ? "bg-[#E8E1FC] text-[#4B3F90]"
      : person?.team === "CLT"
        ? "bg-[rgb(185_138_58/0.12)] text-brass"
        : "bg-rag-unset-bg text-ink-3"

  const href = person ? `/person/${person.id}` : "#"

  return (
    <Link
      to={href}
      className="group relative block overflow-hidden rounded-lg border border-rule bg-card px-[22px] pt-5 pb-[18px] transition hover:-translate-y-px hover:border-rule-strong hover:shadow-card"
    >
      {/* Header: person name + team pill */}
      <div className="mb-[10px] flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-brass">
            {displayName}
            {inactive ? (
              <span className="ml-[6px] text-ink-3">&middot; inactive</span>
            ) : null}
          </div>
          {person?.jobTitle ? (
            <div className="mt-[2px] text-[12.5px] text-ink-3">{person.jobTitle}</div>
          ) : null}
        </div>
        <div
          className={[
            "inline-flex items-center gap-[7px] rounded-full px-[11px] py-1 text-[12px] font-medium",
            ragPillClass(rag),
          ].join(" ")}
        >
          <span className={["h-2 w-2 rounded-full", ragDotClass(rag)].join(" ")} />
          {ragLabel(rag)}
        </div>
      </div>

      {/* Team + department line */}
      <div className="mb-[10px] flex items-center gap-[10px]">
        {person?.team ? (
          <span className={`rounded-full px-[7px] py-[1px] text-[10px] font-medium ${teamPill}`}>
            {person.team}
          </span>
        ) : null}
        {person?.department ? (
          <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-3">
            {person.department}
          </span>
        ) : null}
      </div>

      {/* Objective body */}
      <div
        className="mb-4 overflow-hidden text-[13px] leading-[1.55] text-ink-2"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
        }}
      >
        {primary?.objectiveText || (
          <span className="italic text-ink-3">No objective text recorded.</span>
        )}
      </div>

      {/* Footer: ribbon */}
      <div className="flex items-center justify-between gap-3 border-t border-dashed border-rule pt-[14px]">
        <div className="flex items-center">
          <div className="flex gap-[3px]">
            {history.map((r, i) => (
              <span
                key={i}
                title={`${MONTHS[i]}: ${ragLabel(r)}`}
                className={[
                  "h-[22px] w-[10px] rounded-[2px]",
                  ragCellClass(r),
                  i === CURRENT_MONTH_IDX ? "outline-2 outline-offset-1 outline-brass" : "",
                ].join(" ")}
              />
            ))}
          </div>
          <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.08em] text-ink-3">
            12-mo history
          </span>
        </div>
        {objectives.length > 1 ? (
          <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-3">
            +{objectives.length - 1} more
          </span>
        ) : null}
      </div>
    </Link>
  )
}
